/**
 * DeliveryLabels - Label generator & print queue
 * Select a shipment, preview each box/item label, print via PLX tag system.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import api from '../../../lib/api';
import {
  Tag, Package, ChevronLeft, ChevronRight, Printer,
  Download, Search, Check, Box, List, CheckSquare, Square
} from 'lucide-react';

/* ── PLX Tag generator (reused from AssetQRCode logic) ───────────────────── */
function generatePLXTag(value) {
  const numericMatch = String(value).match(/\d+/);
  if (!numericMatch) throw new Error('PLX value must contain a numeric portion');
  const idBig = BigInt(numericMatch[0]);
  if (idBig === 0n || idBig > 0x1FFFFFFFFFn) throw new Error('ID out of range');
  const safety = Number(idBig % 255n);
  const anchors = 15n;
  const payload = (anchors << 45n) | (idBig << 8n) | BigInt(safety);

  const grid = Array(7).fill(0).map(() => Array(7).fill(0));
  let bitIndex = 0n;
  for (let row = 0; row < 7; row++) {
    for (let col = 0; col < 7; col++) {
      grid[row][col] = Number((payload >> bitIndex) & 1n);
      bitIndex++;
    }
  }

  const cellSize = 50, borderSize = 1, gridSize = 7;
  const totalSize = (gridSize + borderSize * 2) * cellSize;
  const padding = cellSize;
  const canvasSize = totalSize + padding * 2;
  const innerOffset = padding + borderSize * cellSize;

  let svg = `<svg viewBox="0 0 ${canvasSize} ${canvasSize}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;">`;
  svg += `<rect x="0" y="0" width="${canvasSize}" height="${canvasSize}" fill="white"/>`;
  svg += `<rect x="${padding}" y="${padding}" width="${totalSize}" height="${totalSize}" fill="black"/>`;
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      if (grid[row][col] === 1) {
        svg += `<rect x="${innerOffset + col * cellSize}" y="${innerOffset + row * cellSize}" width="${cellSize}" height="${cellSize}" fill="#fff"/>`;
      }
    }
  }
  svg += `</svg>`;
  return svg;
}

/* ── Label preview component ─────────────────────────────────────────────── */
function LabelPreview({ label, shipment, isDark }) {
  const plxSvg = useMemo(() => {
    try { return generatePLXTag(label.plx_code_value); }
    catch { return null; }
  }, [label.plx_code_value]);

  const data = label.label_data || {};

  return (
    <div className="bg-white text-black rounded-lg border-2 border-gray-300 p-4 w-72 mx-auto shadow-lg">
      {/* PLX Tag */}
      <div className="flex justify-center mb-3">
        {plxSvg ? (
          <div
            style={{ width: 140, height: 140 }}
            dangerouslySetInnerHTML={{ __html: plxSvg }}
          />
        ) : (
          <div className="w-36 h-36 bg-gray-200 flex items-center justify-center rounded">
            <Tag size={32} className="text-gray-400" />
          </div>
        )}
      </div>

      {/* Label info */}
      <div className="space-y-1 text-xs border-t border-gray-200 pt-3">
        {data.delivery_no && (
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Delivery #:</span>
            <span className="font-bold">{data.delivery_no}</span>
          </div>
        )}
        {data.shipment_no && (
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Shipment #:</span>
            <span className="font-semibold">{data.shipment_no}</span>
          </div>
        )}
        {data.material_desc && (
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Part:</span>
            <span className="font-semibold truncate max-w-[120px]">{data.material_desc}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500 font-medium">Quantity:</span>
          <span className="font-bold text-sm">{label.label_index} of {label.label_total}</span>
        </div>
        {data.stop_no != null && (
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Stop #:</span>
            <span className="font-bold">{data.stop_no}</span>
          </div>
        )}
        {data.customer_name && (
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Customer:</span>
            <span className="font-semibold truncate max-w-[120px]">{data.customer_name}</span>
          </div>
        )}
        {data.customer_address && (
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Address:</span>
            <span className="font-semibold truncate max-w-[120px]">{data.customer_address}</span>
          </div>
        )}
        {data.carrier_name && (
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Carrier:</span>
            <span className="font-semibold">{data.carrier_name}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
export default function DeliveryLabels() {
  const { isDark } = useTheme();
  const location = useLocation();

  const [shipments, setShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(true);
  const [selectedShipmentId, setSelectedShipmentId] = useState(location.state?.shipmentId || null);
  const [labelType, setLabelType] = useState('box'); // 'box' | 'item'
  const [labels, setLabels] = useState([]);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [selectedLabelIndices, setSelectedLabelIndices] = useState(new Set());
  const [previewIndex, setPreviewIndex] = useState(0);
  const [shipmentSearch, setShipmentSearch] = useState('');
  const [generatingLabels, setGeneratingLabels] = useState(false);

  // Fetch shipments list
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingShipments(true);
        const data = await api.get('/api/delivery/shipments');
        const filtered = (data || []).filter(s => !s.archived);
        setShipments(filtered);
        
        // Auto-select first shipment if none is selected and shipments are available
        // This will only run if selectedShipmentId is null (no shipment from navigation state)
        if (filtered.length > 0) {
          setSelectedShipmentId(prev => prev || filtered[0].id);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingShipments(false);
      }
    };
    load();
  }, []);

  // Fetch/generate labels when shipment or type changes
  useEffect(() => {
    if (!selectedShipmentId) { setLabels([]); return; }
    const load = async () => {
      try {
        setLoadingLabels(true);
        const data = await api.get(`/api/delivery/shipments/${selectedShipmentId}/labels?type=${labelType}`);
        setLabels(data || []);
        setSelectedLabelIndices(new Set((data || []).map((_, i) => i)));
        setPreviewIndex(0);
      } catch (err) {
        console.error(err);
        setLabels([]);
      } finally {
        setLoadingLabels(false);
      }
    };
    load();
  }, [selectedShipmentId, labelType]);

  const selectedShipment = useMemo(
    () => shipments.find(s => s.id === selectedShipmentId),
    [shipments, selectedShipmentId]
  );

  const filteredShipments = useMemo(() => {
    const q = shipmentSearch.toLowerCase();
    return shipments.filter(s =>
      !q ||
      (s.delivery_no || '').toLowerCase().includes(q) ||
      (s.shipment_no || '').toLowerCase().includes(q) ||
      (s.customer_name || '').toLowerCase().includes(q)
    );
  }, [shipments, shipmentSearch]);

  const selectedLabels = useMemo(
    () => labels.filter((_, i) => selectedLabelIndices.has(i)),
    [labels, selectedLabelIndices]
  );

  const toggleLabel = (i) => {
    setSelectedLabelIndices(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const selectAll = () => setSelectedLabelIndices(new Set(labels.map((_, i) => i)));
  const deselectAll = () => setSelectedLabelIndices(new Set());

  const printLabels = (labelsToPrint) => {
    if (!labelsToPrint.length) return;
    const printWindow = window.open('', '', 'width=800,height=900');
    const labelHtml = labelsToPrint.map(label => {
      let plxSvg = '';
      try { plxSvg = generatePLXTag(label.plx_code_value); } catch {}
      const d = label.label_data || {};
      return `
        <div class="label">
          <div class="plx">${plxSvg}</div>
          <div class="fields">
            ${d.delivery_no  ? `<div class="row"><span class="key">Delivery #:</span><span class="val">${d.delivery_no}</span></div>` : ''}
            ${d.shipment_no  ? `<div class="row"><span class="key">Shipment #:</span><span class="val">${d.shipment_no}</span></div>` : ''}
            ${d.material_desc ? `<div class="row"><span class="key">Part:</span><span class="val">${d.material_desc}</span></div>` : ''}
            <div class="row box-of"><span class="key">Quantity:</span><span class="val">${label.label_index} of ${label.label_total}</span></div>
            ${d.stop_no != null ? `<div class="row"><span class="key">Stop #:</span><span class="val">${d.stop_no}</span></div>` : ''}
            ${d.customer_name ? `<div class="row"><span class="key">Customer:</span><span class="val">${d.customer_name}</span></div>` : ''}
            ${d.customer_address ? `<div class="row"><span class="key">Address:</span><span class="val">${d.customer_address}</span></div>` : ''}
            ${d.carrier_name ? `<div class="row"><span class="key">Carrier:</span><span class="val">${d.carrier_name}</span></div>` : ''}
          </div>
        </div>`;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Delivery Labels</title>
      <style>
        @page { size: 4in 6in; margin: 0; }
        body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
        .label { width: 4in; height: 6in; padding: 0.2in; box-sizing: border-box; border: 2px solid #000; display: flex; flex-direction: column; align-items: center; page-break-after: always; }
        .plx { width: 2.5in; height: 2.5in; margin-bottom: 0.15in; }
        .fields { width: 100%; }
        .row { display: flex; justify-content: space-between; font-size: 11px; margin: 3px 0; border-bottom: 1px solid #eee; padding-bottom: 2px; }
        .key { color: #555; font-weight: 600; }
        .val { font-weight: 700; text-align: right; max-width: 2in; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .box-of .val { font-size: 14px; font-weight: 900; }
        @media print { body { margin: 0; } }
      </style></head><body>${labelHtml}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);

    // Mark as printed
    if (selectedShipmentId) {
      api.patch(`/api/delivery/shipments/${selectedShipmentId}/labels/mark-printed`, {
        labelIds: labelsToPrint.map(l => l.id).filter(Boolean)
      }).catch(() => {});
    }
  };

  const bg = isDark ? 'bg-gray-900' : 'bg-gray-50';
  const panelBg = isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200 shadow-sm';

  return (
    <div className={`flex h-full overflow-hidden ${bg}`}>

      {/* LEFT: Shipment selector */}
      <div className={`w-72 flex-shrink-0 flex flex-col border-r ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        <div className={`px-4 py-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <h2 className={`font-semibold text-sm mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Select Shipment</h2>
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <Search size={13} className="text-gray-400" />
            <input
              value={shipmentSearch}
              onChange={e => setShipmentSearch(e.target.value)}
              placeholder="Search..."
              className={`flex-1 bg-transparent text-xs outline-none ${isDark ? 'text-gray-100 placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loadingShipments ? (
            <div className="flex justify-center py-8">
              <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredShipments.length === 0 ? (
            <p className={`text-xs text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No shipments found</p>
          ) : (
            filteredShipments.map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedShipmentId(s.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                  selectedShipmentId === s.id
                    ? 'border-violet-500 bg-violet-900/20 text-violet-300'
                    : isDark
                      ? 'border-gray-700 hover:bg-gray-800 text-gray-300'
                      : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                }`}
              >
                <div className="text-sm font-medium">{s.delivery_no || s.shipment_no || s.id.slice(0, 8)}</div>
                <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {s.customer_name || '—'}
                  {s.box_quantity ? ` · Qty: ${s.box_quantity}` : ''}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Label type selector */}
        {selectedShipmentId && (
          <div className={`p-3 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Label Type</div>
            <div className="flex gap-2">
              <button
                onClick={() => setLabelType('box')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  labelType === 'box'
                    ? 'bg-violet-600 border-violet-500 text-white'
                    : isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Box size={13} /> Quantity
              </button>
              <button
                onClick={() => setLabelType('item')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  labelType === 'item'
                    ? 'bg-violet-600 border-violet-500 text-white'
                    : isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <List size={13} /> Item
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CENTER: Label preview */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!selectedShipmentId ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <Tag size={48} className={isDark ? 'text-gray-700' : 'text-gray-300'} />
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Select a shipment to generate labels</p>
          </div>
        ) : loadingLabels ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : labels.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Package size={40} className={isDark ? 'text-gray-600' : 'text-gray-300'} />
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              No labels for this shipment
            </p>
          </div>
        ) : (
          <>
            {/* Preview area */}
            <div className="flex-1 flex flex-col items-center justify-center p-6 overflow-y-auto">
              <LabelPreview
                label={labels[previewIndex]}
                shipment={selectedShipment}
                isDark={isDark}
              />

              {/* Navigation */}
              <div className="flex items-center gap-4 mt-6">
                <button
                  onClick={() => setPreviewIndex(i => Math.max(0, i - 1))}
                  disabled={previewIndex === 0}
                  className={`p-2 rounded-lg border transition-colors ${
                    previewIndex === 0
                      ? isDark ? 'border-gray-700 text-gray-600' : 'border-gray-200 text-gray-300'
                      : isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <ChevronLeft size={18} />
                </button>
                <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {previewIndex + 1} of {labels.length}
                </span>
                <button
                  onClick={() => setPreviewIndex(i => Math.min(labels.length - 1, i + 1))}
                  disabled={previewIndex === labels.length - 1}
                  className={`p-2 rounded-lg border transition-colors ${
                    previewIndex === labels.length - 1
                      ? isDark ? 'border-gray-700 text-gray-600' : 'border-gray-200 text-gray-300'
                      : isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>

            {/* Print controls */}
            <div className={`flex-shrink-0 border-t px-6 py-4 flex items-center gap-3 ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {selectedLabelIndices.size} of {labels.length} selected
              </span>
              <button
                onClick={printLabels.bind(null, selectedLabels)}
                disabled={selectedLabelIndices.size === 0}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white rounded-lg font-medium transition-colors text-sm"
              >
                <Printer size={15} />
                Print Selected ({selectedLabelIndices.size})
              </button>
              <button
                onClick={printLabels.bind(null, labels)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-700 hover:bg-violet-600 text-white rounded-lg font-medium transition-colors text-sm"
              >
                <Printer size={15} />
                Print All ({labels.length})
              </button>
            </div>
          </>
        )}
      </div>

      {/* RIGHT: Label checklist */}
      {selectedShipmentId && labels.length > 0 && (
        <div className={`w-56 flex-shrink-0 flex flex-col border-l ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
          <div className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Labels to Print
            </span>
            <div className="flex gap-1">
              <button onClick={selectAll} className="text-xs text-violet-400 hover:text-violet-300">All</button>
              <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>/</span>
              <button onClick={deselectAll} className={`text-xs ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>None</button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {labels.map((label, i) => (
              <button
                key={i}
                onClick={() => { toggleLabel(i); setPreviewIndex(i); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-colors text-left ${
                  previewIndex === i
                    ? 'border-violet-500 bg-violet-900/20'
                    : isDark ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                {selectedLabelIndices.has(i)
                  ? <CheckSquare size={14} className="text-violet-400 flex-shrink-0" />
                  : <Square size={14} className={`flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                }
                <div>
                  <div className={`text-xs font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    {label.label_index} of {label.label_total}
                  </div>
                  {label.printed && (
                    <div className="flex items-center gap-1 text-xs text-green-400 mt-0.5">
                      <Check size={10} /> Printed
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
